/**
 * saas-mock.ts — In-browser mock store for the ɳSentry SaaS REST API
 *
 * Purpose:    Serve realistic demo data for every /v1 endpoint when
 *             VITE_API_MOCK==='1' or the real API is unreachable, so the
 *             app zone is fully demoable before the backend box (W2) ships.
 *             Enforces tier quotas (e.g. 11th monitor on free tier → 402).
 * Inputs:     CRUD calls from saas-client.ts
 * Outputs:    Result<T, SentryError> per endpoint; mutations persist to
 *             localStorage key `nsentry_mock_store`
 * Constraints: Browser-only; no network. Single free-tier demo tenant.
 * SOT:        nsentry SaaS plan — mock fallback contract
 */
import { ok, err, type Result } from './result'
import type { SentryError } from './sentry-error'
import { TIERS, type TierId } from './tiers'
import type {
  Monitor,
  MonitorCheck,
  Incident,
  IncidentEvent,
  StatusPage,
  StatusPageSubscriber,
  PublicStatusPage,
  ApiKey,
  Overview,
  BillingInfo,
} from './saas-client'

// v3: monitors gained sslExpiresAt, status pages gained customDomain, and the
// store gained subscribers — new key so stale persisted stores never surface
// partially-shaped records.
const STORE_KEY = 'nsentry_mock_store_v3'

interface MockStore {
  tier: TierId
  monitors: Monitor[]
  incidents: Incident[]
  statusPages: StatusPage[]
  subscribers: Array<StatusPageSubscriber & { pageId: string }>
  apiKeys: ApiKey[]
  overview: Overview
}

function daysFromNow(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString()
}

function seedStore(): MockStore {
  const now = new Date().toISOString()
  return {
    tier: 'free',
    monitors: [
      { id: 'mon-1', name: 'Marketing site', url: 'https://nself.org', intervalSeconds: 300, status: 'up', latencyMs: 84, lastCheckedAt: now, sslExpiresAt: daysFromNow(62) },
      { id: 'mon-2', name: 'API', url: 'https://api.nself.org/health', intervalSeconds: 300, status: 'up', latencyMs: 122, lastCheckedAt: now, sslExpiresAt: daysFromNow(29) },
      { id: 'mon-3', name: 'Docs', url: 'https://docs.nself.org', intervalSeconds: 300, status: 'degraded', latencyMs: 890, lastCheckedAt: now, sslExpiresAt: daysFromNow(11) },
    ],
    incidents: [
      { id: 'inc-1', title: 'Docs elevated latency', status: 'open', severity: 'minor', monitorId: 'mon-3', startedAt: now },
      { id: 'inc-2', title: 'API brief outage', status: 'resolved', severity: 'major', monitorId: 'mon-2', startedAt: now, resolvedAt: now },
    ],
    statusPages: [
      {
        id: 'sp-1',
        slug: 'demo',
        title: 'ɳSentry Demo Status',
        components: [
          { id: 'c-1', name: 'Marketing site', status: 'operational' },
          { id: 'c-2', name: 'API', status: 'operational' },
          { id: 'c-3', name: 'Docs', status: 'degraded' },
        ],
        published: true,
        customDomain: null,
      },
    ],
    subscribers: [
      { id: 'sub-1', pageId: 'sp-1', email: 'ops@example.com', createdAt: now },
    ],
    apiKeys: [
      { id: 'key-1', name: 'Default key', prefix: 'nsk_demo1', scope: 'read', createdAt: now },
    ],
    overview: {
      monitors: { total: 3, up: 2, down: 0, paused: 0, pending: 1 },
      incidentsOpen: 1,
      uptimePct24h: 99.98,
      errors: {
        eventsThisMonth: 1_248,
        topGroups: [
          { message: 'TypeError: Cannot read properties of undefined', count: 412, lastSeen: now },
          { message: 'NetworkError: fetch failed', count: 201, lastSeen: now },
        ],
      },
      rum: {
        pageviewsThisMonth: 3_920,
        p75LcpMs: 1840,
        p75InpMs: 140,
        errorRatePct: 0.6,
      },
    },
  }
}

// In-memory fallback used when localStorage is unavailable or non-functional
// (e.g. some jsdom test environments provide a localStorage object whose
// methods throw). Keeps the mock store usable for tests and any environment
// without persistent storage — mutations just won't survive a full reload.
let memoryStore: MockStore | null = null

function load(): MockStore {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (raw) return JSON.parse(raw) as MockStore
  } catch {
    // localStorage unavailable/non-functional — use in-memory fallback below
  }
  if (memoryStore) return memoryStore
  const seeded = seedStore()
  save(seeded)
  return seeded
}

function save(store: MockStore): void {
  memoryStore = store
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store))
  } catch {
    // localStorage unavailable — memoryStore above keeps mutations working
    // for the lifetime of this module (won't persist across reloads)
  }
}

/**
 * Test-only reset of the in-memory fallback store. Production code never
 * calls this — it exists so unit tests can start each case from a fresh
 * seed without needing a full module re-import (which is expensive across
 * this monorepo's dependency graph).
 */
export function __resetMockStoreForTests(): void {
  memoryStore = null
}

function quotaError(resource: string, max: number): SentryError {
  return {
    type: 'bundle_required',
    message: `Free tier limit reached: ${max} ${resource} max. Upgrade to ɳSentry Bundle for more.`,
  }
}

let idCounter = 100
function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

export const mockApi = {
  listMonitors(): Result<Monitor[], SentryError> {
    return ok(load().monitors)
  },

  createMonitor(input: { name: string; url: string; intervalSeconds: number }): Result<Monitor, SentryError> {
    const store = load()
    const quota = TIERS[store.tier].monitors
    if (store.monitors.length >= quota.max) {
      return err(quotaError('monitors', quota.max))
    }
    if (input.intervalSeconds < quota.minIntervalSeconds) {
      return err({ type: 'bundle_required', message: `Free tier minimum check interval is ${quota.minIntervalSeconds / 60} minutes.` })
    }
    const monitor: Monitor = {
      id: nextId('mon'),
      name: input.name,
      url: input.url,
      intervalSeconds: input.intervalSeconds,
      status: 'pending',
      latencyMs: null,
      lastCheckedAt: null,
      sslExpiresAt: null,
    }
    store.monitors.push(monitor)
    save(store)
    return ok(monitor)
  },

  updateMonitor(id: string, patch: Partial<Monitor>): Result<Monitor, SentryError> {
    const store = load()
    const idx = store.monitors.findIndex((m) => m.id === id)
    if (idx === -1) return err({ type: 'config_error', message: 'Monitor not found' })
    store.monitors[idx] = { ...store.monitors[idx], ...patch }
    save(store)
    return ok(store.monitors[idx])
  },

  /** Synthetic per-monitor probe history: deterministic, mostly-up series. */
  monitorChecks(id: string): Result<MonitorCheck[], SentryError> {
    const store = load()
    const monitor = store.monitors.find((m) => m.id === id)
    if (!monitor) return err({ type: 'config_error', message: 'Monitor not found' })
    const base = monitor.latencyMs ?? 120
    const now = Date.now()
    const checks: MonitorCheck[] = Array.from({ length: 60 }, (_, i) => {
      // Deterministic pseudo-noise so charts look alive but tests stay stable.
      const wobble = Math.round(Math.sin(i * 1.7) * 0.25 * base)
      const down = monitor.status === 'down' && i < 3
      return {
        checkedAt: new Date(now - i * monitor.intervalSeconds * 1000).toISOString(),
        status: down ? 'down' : 'up',
        latencyMs: down ? null : Math.max(10, base + wobble),
      }
    })
    return ok(checks)
  },

  deleteMonitor(id: string): Result<void, SentryError> {
    const store = load()
    store.monitors = store.monitors.filter((m) => m.id !== id)
    save(store)
    return ok(undefined)
  },

  listIncidents(): Result<Incident[], SentryError> {
    return ok(load().incidents)
  },

  createIncident(input: { title: string; severity: Incident['severity']; monitorId?: string }): Result<Incident, SentryError> {
    const store = load()
    const incident: Incident = {
      id: nextId('inc'),
      title: input.title,
      severity: input.severity,
      status: 'open',
      monitorId: input.monitorId,
      startedAt: new Date().toISOString(),
    }
    store.incidents.unshift(incident)
    save(store)
    return ok(incident)
  },

  /** Derive a minimal timeline from the incident's own lifecycle fields. */
  incidentTimeline(id: string): Result<IncidentEvent[], SentryError> {
    const store = load()
    const incident = store.incidents.find((i) => i.id === id)
    if (!incident) return err({ type: 'config_error', message: 'Incident not found' })
    const events: IncidentEvent[] = [
      { at: incident.startedAt, kind: 'opened', message: `Incident opened: ${incident.title}` },
    ]
    if (incident.status !== 'open') {
      events.push({ at: incident.resolvedAt ?? incident.startedAt, kind: incident.status, message: `Incident ${incident.status}` })
    }
    return ok(events)
  },

  ackIncident(id: string): Result<Incident, SentryError> {
    const store = load()
    const idx = store.incidents.findIndex((i) => i.id === id)
    if (idx === -1) return err({ type: 'config_error', message: 'Incident not found' })
    store.incidents[idx] = { ...store.incidents[idx], status: 'acknowledged' }
    save(store)
    return ok(store.incidents[idx])
  },

  resolveIncident(id: string): Result<Incident, SentryError> {
    const store = load()
    const idx = store.incidents.findIndex((i) => i.id === id)
    if (idx === -1) return err({ type: 'config_error', message: 'Incident not found' })
    store.incidents[idx] = { ...store.incidents[idx], status: 'resolved', resolvedAt: new Date().toISOString() }
    save(store)
    return ok(store.incidents[idx])
  },

  listStatusPages(): Result<StatusPage[], SentryError> {
    return ok(load().statusPages)
  },

  createStatusPage(input: { slug: string; title: string }): Result<StatusPage, SentryError> {
    const store = load()
    const max = TIERS[store.tier].statusPages
    if (store.statusPages.length >= max) {
      return err(quotaError('status pages', max))
    }
    if (store.statusPages.some((p) => p.slug === input.slug)) {
      return err({ type: 'config_error', message: 'Slug already in use' })
    }
    const page: StatusPage = { id: nextId('sp'), slug: input.slug, title: input.title, components: [], published: false, customDomain: null }
    store.statusPages.push(page)
    save(store)
    return ok(page)
  },

  updateStatusPage(id: string, patch: Partial<StatusPage>): Result<StatusPage, SentryError> {
    const store = load()
    const idx = store.statusPages.findIndex((p) => p.id === id)
    if (idx === -1) return err({ type: 'config_error', message: 'Status page not found' })
    store.statusPages[idx] = { ...store.statusPages[idx], ...patch }
    save(store)
    return ok(store.statusPages[idx])
  },

  deleteStatusPage(id: string): Result<void, SentryError> {
    const store = load()
    store.statusPages = store.statusPages.filter((p) => p.id !== id)
    save(store)
    return ok(undefined)
  },

  listSubscribers(pageId: string): Result<StatusPageSubscriber[], SentryError> {
    const store = load()
    return ok(store.subscribers.filter((s) => s.pageId === pageId).map(({ pageId: _pageId, ...s }) => s))
  },

  addSubscriber(pageId: string, email: string): Result<StatusPageSubscriber, SentryError> {
    const store = load()
    if (store.subscribers.some((s) => s.pageId === pageId && s.email === email)) {
      return err({ type: 'config_error', message: 'That email is already subscribed' })
    }
    const sub = { id: nextId('sub'), pageId, email, createdAt: new Date().toISOString() }
    store.subscribers.push(sub)
    save(store)
    const { pageId: _pageId, ...rest } = sub
    return ok(rest)
  },

  removeSubscriber(id: string): Result<void, SentryError> {
    const store = load()
    store.subscribers = store.subscribers.filter((s) => s.id !== id)
    save(store)
    return ok(undefined)
  },

  publicStatusPage(slug: string): Result<PublicStatusPage, SentryError> {
    const store = load()
    const page = store.statusPages.find((p) => p.slug === slug && p.published)
    if (!page) return err({ type: 'config_error', message: 'Status page not found' })
    const overallStatus = page.components.some((c) => c.status === 'down')
      ? ('down' as const)
      : page.components.some((c) => c.status === 'degraded')
        ? ('degraded' as const)
        : ('operational' as const)
    return ok({
      title: page.title,
      overallStatus,
      components: page.components.map((c) => ({ ...c, uptimePercent: null })),
      incidents: store.incidents
        .filter((i) => i.status !== 'resolved')
        .map((i) => ({ title: i.title, status: i.status, startedAt: i.startedAt })),
    })
  },

  overview(): Result<Overview, SentryError> {
    return ok(load().overview)
  },

  listApiKeys(): Result<ApiKey[], SentryError> {
    return ok(load().apiKeys)
  },

  createApiKey(input: { name: string; scope: 'read' | 'full' }): Result<ApiKey & { key: string }, SentryError> {
    const store = load()
    if (input.scope === 'full' && TIERS[store.tier].apiAccess === 'read-only') {
      return err({ type: 'bundle_required', message: 'Full API access requires ɳSentry Bundle.' })
    }
    const secret = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    const prefix = `nsk_${secret.slice(0, 8)}`
    const apiKey: ApiKey = { id: nextId('key'), name: input.name, prefix, scope: input.scope, createdAt: new Date().toISOString() }
    store.apiKeys.push(apiKey)
    save(store)
    return ok({ ...apiKey, key: `${prefix}_${secret}` })
  },

  revokeApiKey(id: string): Result<void, SentryError> {
    const store = load()
    store.apiKeys = store.apiKeys.filter((k) => k.id !== id)
    save(store)
    return ok(undefined)
  },

  billing(): Result<BillingInfo, SentryError> {
    const store = load()
    return ok({
      tier: store.tier,
      usage: {
        monitors: store.monitors.length,
        statusPages: store.statusPages.length,
        errorEventsThisMonth: store.overview.errors.eventsThisMonth,
        rumPageviewsThisMonth: store.overview.rum.pageviewsThisMonth,
        heartbeats: 1,
      },
    })
  },

  checkout(): Result<{ url: string }, SentryError> {
    return ok({ url: 'https://nself.org/sentry' })
  },
}
